import { useTranslation } from "react-i18next";
import { Col } from "../../../components/col";
import { Row } from "../../../components/row";
import type { EGiftCardStatisticsResponse } from "../types/eGiftCard.types";
import {
  LowStockAlertIcon,
  TotalBrandGiftCardsIcon,
  TotalExchangedIcon,
  TotalFundsIcon,
} from "./icons";
import EGiftCardSummaryCard from "./EGiftCardSummaryCard";

type EGiftCardStatisticsProps = { statistics: EGiftCardStatisticsResponse | null };
const iconWrapperClassName =
  "w-6 h-6 p-1 rounded-md flex items-center justify-center bg-[#F2FCFA] dark:bg-emerald-900/30";
const formatCurrency = (amount: number, currency = "USD"): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 0 }).format(
    amount,
  );

const EGiftCardStatistics = ({ statistics }: EGiftCardStatisticsProps) => {
  const { t } = useTranslation();
  const currency = statistics?.currency || "USD";
  const forecastRemaining = statistics?.forecast_remaining || 0;
  const financialMetrics = [
    [t("eGiftCard.statistics.actualSpent"), statistics?.actual_spent || 0],
    [t("eGiftCard.statistics.committed"), statistics?.committed_amount || 0],
    [t("eGiftCard.statistics.requested"), statistics?.requested_amount || 0],
    [t("eGiftCard.statistics.refunds"), statistics?.refund_amount || 0],
  ] as const;
  return (
    <div className="mb-6 space-y-4">
      <Row>
        <Col className="flex-1">
          <EGiftCardSummaryCard
            icon={
              <div className={iconWrapperClassName}>
                <TotalBrandGiftCardsIcon />
              </div>
            }
            title={t("eGiftCard.statistics.totalBrandGiftCards")}
            value={statistics?.total_gift_card || 0}
          />
        </Col>
        <Col className="flex-1">
          <EGiftCardSummaryCard
            icon={
              <div className={iconWrapperClassName}>
                <TotalExchangedIcon />
              </div>
            }
            title={t("eGiftCard.statistics.totalExchanged")}
            value={statistics?.total_exchanged || 0}
          />
        </Col>
        <Col className="flex-1">
          <EGiftCardSummaryCard
            icon={
              <div className={iconWrapperClassName}>
                <TotalFundsIcon />
              </div>
            }
            title={t("eGiftCard.statistics.tangoBalance")}
            value={formatCurrency(
              statistics?.tango_balance || statistics?.total_fund || 0,
              currency,
            )}
          />
        </Col>
        <Col className="flex-1">
          <EGiftCardSummaryCard
            icon={
              <div className={iconWrapperClassName}>
                <LowStockAlertIcon />
              </div>
            }
            title={t("eGiftCard.statistics.lowStockAlerts")}
            value={statistics?.total_low_stock || 0}
          />
        </Col>
      </Row>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {financialMetrics.map(([title, value]) => (
          <div
            key={title}
            className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-[#394150] dark:bg-[#212936]"
          >
            <span className="block text-sm text-gray-500 dark:text-gray-400">{title}</span>
            <span className="mt-1 block text-xl font-semibold text-gray-900 dark:text-gray-100">
              {formatCurrency(value, currency)}
            </span>
          </div>
        ))}
        <div
          className={
            "rounded-lg border p-4 shadow-sm " +
            (forecastRemaining < 0
              ? "border-red-200 bg-red-50 dark:border-red-900/60 dark:bg-red-950/30"
              : "border-emerald-200 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/30")
          }
        >
          <span className="block text-sm text-gray-600 dark:text-gray-300">
            {t("eGiftCard.statistics.forecastRemaining")}
          </span>
          <span
            className={
              "mt-1 block text-xl font-semibold " +
              (forecastRemaining < 0
                ? "text-red-700 dark:text-red-300"
                : "text-emerald-700 dark:text-emerald-300")
            }
          >
            {formatCurrency(forecastRemaining, currency)}
          </span>
          <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
            {forecastRemaining < 0
              ? t("eGiftCard.statistics.insufficientFund")
              : t("eGiftCard.statistics.availableAfterCommitments")}
          </span>
        </div>
      </div>
    </div>
  );
};
export default EGiftCardStatistics;
